import { useId } from "react";

import { cn } from "@/frontend/lib/utils";

import { Checkbox } from "./checkbox";
import { Label } from "./label";

export interface CheckboxGroupOption {
    label: string;
    value: string;
    disabled?: boolean;
}

export interface CheckboxGroupProps {
    options: CheckboxGroupOption[];
    value: string[];
    onValueChange: (value: string[]) => void;
    disabled?: boolean;
    orientation?: "horizontal" | "vertical";
    className?: string;
}

function CheckboxGroup({
    options,
    value,
    onValueChange,
    disabled,
    orientation = "vertical",
    className,
}: CheckboxGroupProps) {
    const groupId = useId();

    const handleToggle = (optionValue: string, checked: boolean) => {
        if (checked) {
            onValueChange([...value, optionValue]);
        } else {
            onValueChange(value.filter((v) => v !== optionValue));
        }
    };

    return (
        <fieldset
            className={cn(
                "flex gap-3 border-none p-0 m-0",
                orientation === "vertical" ? "flex-col" : "flex-row flex-wrap",
                className,
            )}
        >
            {options.map((option) => {
                const optionId = `${groupId}-${option.value}`;
                const isChecked = value.includes(option.value);
                const isDisabled = disabled || option.disabled;

                return (
                    <div key={option.value} className="flex items-center gap-2">
                        <Checkbox
                            id={optionId}
                            checked={isChecked}
                            onCheckedChange={(checked) =>
                                handleToggle(option.value, checked === true)
                            }
                            disabled={isDisabled}
                        />
                        <Label
                            htmlFor={optionId}
                            className={cn(
                                "font-normal leading-none",
                                isDisabled && "cursor-not-allowed opacity-50",
                            )}
                        >
                            {option.label}
                        </Label>
                    </div>
                );
            })}
        </fieldset>
    );
}

export { CheckboxGroup };
